import React, { useState } from "react";
import { Box, Checkbox, Stack, TextField, Typography } from "@mui/material";
import { borderStyle, cardStyle, contentBoxStyle } from "../shared/Theme";
import Sidebar from "../elements/Sidebar";
import Header from "../elements/Header";
import { blueGradientBackground, pink } from "../shared/colours";
import { useRef } from "react";
import { ArrowCircleUpSharp, DoubleArrow } from "@mui/icons-material";
import SubmitModal from "../elements/Admin/SubmitModal";
import formService from "../services/formService";
import AccountGuard from "../elements/Admin/AccountGuard";

const PhishingSim = () => {

	let [ showSubmitModal, setShowSubmitModal ] = useState(false);
	let [ sentSuccess, setSentSuccess ] = useState(false);
	
	const numberOfUsers = useRef(null);
	const numberOfGroups = useRef(null);
	const emailOnly = useRef(null);
	const landingPage = useRef(null);
	const yesResponse = useRef(null);
	const responseMessage = useRef(null);
	const noAutomation = useRef(null);
	const yesAutomation = useRef(null);
	const monthlyFrequency = useRef(null);
	const quarterlyFrequency = useRef(null);
	
	const handleSubmitClick = async () => {
		setSentSuccess(false);
		setShowSubmitModal(true);
		await formService.phishing({
			numberOfUsers: numberOfUsers.current.value,
			numberOfGroups: numberOfGroups.current.value,
			emailOnly: emailOnly.current.checked,
			landingPage: landingPage.current.checked,
			yesResponse: yesResponse.current.checked,
			responseMessage: responseMessage.current.value,
			noAutomation: noAutomation.current.checked,
			yesAutomation: yesAutomation.current.checked,
			monthlyFrequency: monthlyFrequency.current.checked,
			quarterlyFrequency: quarterlyFrequency.current.checked,
		});
		setSentSuccess(true);
		setTimeout(() => {
			setShowSubmitModal(false);
		}, 1500);
	};
	
	const handleSubmitModalClose = () => {
		setSentSuccess(false);
		setShowSubmitModal(false);
	};
	
	return (
		<Box sx={{ display: "flex" }}>
			<Sidebar/>
			<AccountGuard requiredService="usecure"/>
			<Box component="main" sx={{flexGrow: 1, bgcolor: "background.default"}}>
				<Header pageTitle="Phishing Sim"/>
				<Box sx={contentBoxStyle}>
					<Stack sx={{ width: "80%", margin: "auto" }} justifyContent="center">
						<Stack direction="row" sx={{ width: "100%", borderBottom: "2px solid", ...borderStyle }}>
							<Box sx={{ width: "50%", borderRight: "solid 2px", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Simulated Phishing
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "2em",
											fontSize: "0.8em"
										}}
									>
										Phishing is estimated to be the first step in over
										<span style={{ fontWeight: "bold", color: pink }}> 90% </span>
										of successful Cyber incidents<br/>
										In order to help reduce your Organisation's exposure to Phishing,
										we provide a powerful <br/>
										<span style={{ fontWeight: "bold", color: pink }}>Simulated Phishing </span>
										capability. This is where we create deceptive emails, designed to be<br/>
										very similar to real malicious emails, and send them out to your
										people to gauge their<br/>
										ability to recognise and respond to Phishing and similar email attacks.<br/><br/>
										In order to set this up accurately, please answer the following questions and we<br/>
										will contact you to initiate the Simulated Phishing Programme
									</Typography>
								</Stack>
							</Box>
							<Box sx={{ width: "50%", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Configure Simulated Phishing
									</Typography>
									<Stack
										direction="row" spacing={6}
										justifyContent="center"
										sx={{ width: "100%", marginTop: "2em" }}
									>
										<Stack direction="column" spacing={1} sx={{ width: "15%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Number of<br/>
												Users
											</Typography>
											<TextField
												inputRef={numberOfUsers}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "100%",
													'& .MuiOutlinedInput-root.Mui-focused': {
														"& > fieldset": {
															border: "0"
														}
													},
													'&:hover fieldset': {
														border: "0"
													}
												}}
											/>
										</Stack>
										<Stack direction="column" spacing={1} sx={{ width: "15%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Number of<br/>
												Groups
											</Typography>
											<TextField
												inputRef={numberOfGroups}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "100%",
													'& .MuiOutlinedInput-root.Mui-focused': {
														"& > fieldset": {
															border: "0"
														}
													},
													'&:hover fieldset': {
														border: "0"
													}
												}}
											/>
										</Stack>
									</Stack>
								</Stack>
							</Box>
						</Stack>
						<Stack direction="row" sx={{ width: "100%", borderBottom: "2px solid", ...borderStyle }}>
							<Box sx={{ width: "50%", borderRight: "solid 2px", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Email Only or Email + Landing Page?
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1.25em",
											fontSize: "0.8em"
										}}
									>
										A Simulated Phishing Email with an associated Landing Page to click<br/>
										through to is more convincing and improves Reporting + Analytics
									</Typography>
									<Stack
										direction="row" spacing={6}
										justifyContent="center"
										sx={{ width: "100%", marginTop: "2em", marginBottom: "2em" }}
									>
										<Stack direction="column" spacing={1} alignItems="center" sx={{ width: "15%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Email<br/>
												Only
											</Typography>
											<Checkbox
												inputRef={emailOnly}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "3.5em",
													height: "3.5em",
													'&.Mui-checked': {
														color: pink
													}
												}}
												icon={<React.Fragment/>}
											/>
										</Stack>
										<Stack direction="column" spacing={1} alignItems="center" sx={{ width: "15%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												+ Landing<br/>
												Page
											</Typography>
											<Checkbox
												inputRef={landingPage}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "3.5em",
													height: "3.5em",
													'&.Mui-checked': {
														color: pink
													}
												}}
												icon={<React.Fragment/>}
											/>
										</Stack>
									</Stack>
								</Stack>
							</Box>
							<Box sx={{ width: "50%", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Will Users Receive a Response?
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1.25em",
											fontSize: "0.8em"
										}}
									>
										When a User clicks a link or enters information, we can generate a response<br/>
										that the User will see on screen - for example
										<span style={{ fontWeight: "bold", fontStyle: "italic"}}>
											{`"Oops, this is your IT Team..."`}
										</span>
									</Typography>
									<Stack
										direction="row" spacing={2}
										justifyContent="center"
										sx={{ width: "100%", marginTop: "2em", marginBottom: "2em" }}
									>
										<Stack direction="column" spacing={1} alignItems="center" sx={{ width: "12%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Receive<br/>
												Response
											</Typography>
											<Checkbox
												inputRef={yesResponse}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "3.5em",
													height: "3.5em",
													'&.Mui-checked': {
														color: pink
													}
												}}
												icon={<React.Fragment/>}
											/>
										</Stack>
										<DoubleArrow sx={{ position: "relative", color: pink, top: "3em" }}/>
										<Stack direction="column" spacing={1} sx={{ width: "35%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Response<br/>
												Message
											</Typography>
											<TextField
												inputRef={responseMessage}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "100%",
													'& .MuiOutlinedInput-root.Mui-focused': {
														"& > fieldset": {
															border: "0"
														}
													},
													'&:hover fieldset': {
														border: "0"
													}
												}}
											/>
										</Stack>
									</Stack>
								</Stack>
							</Box>
						</Stack>
						<Stack direction="row" sx={{ width: "100%" }}>
							<Box sx={{ width: "50%", borderRight: "solid 2px", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Automated Simulated Phishing
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1.25em",
											fontSize: "0.8em"
										}}
									>
										We can set up an
										<span style={{ fontWeight: "bold" }}>Auto Phish </span>
										feature which will automate the sending<br/>
										of Simulated Phishing to your Users. If you wish to enable this, just set<br/>
										your frequency below
									</Typography>
									<Stack
										direction="row" spacing={6}
										justifyContent="center"
										sx={{ width: "100%", marginTop: "2em", marginBottom: "2em" }}
									>
										<Stack direction="column" spacing={1} alignItems="center" sx={{ width: "12%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												No
											</Typography>
											<Checkbox
												inputRef={noAutomation}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "3.5em",
													height: "3.5em",
													'&.Mui-checked': {
														color: pink
													}
												}}
												icon={<React.Fragment/>}
											/>
										</Stack>
										<Stack direction="column" spacing={1} alignItems="center" sx={{ width: "12%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Yes
											</Typography>
											<Checkbox
												inputRef={yesAutomation}
												sx={{
													border: `2px solid ${pink}`,
													borderRadius: "0.5em",
													width: "3.5em",
													height: "3.5em",
													'&.Mui-checked': {
														color: pink
													}
												}}
												icon={<React.Fragment/>}
											/>
										</Stack>
										<Stack direction="column" spacing={1} sx={{ width: "12%" }}>
											<Typography sx={{ fontWeight: "bold", width: "100%", textAlign: "center" }}>
												Yes
											</Typography>
											<Stack direction="row">
												<Checkbox
													inputRef={monthlyFrequency}
													sx={{
														border: `2px solid ${pink}`,
														borderRadius: "0.25em",
														width: "1.2em",
														height: "1.2em",
														marginRight: "0.5em",
														'&.Mui-checked': {
															color: pink
														}
													}}
													icon={<React.Fragment/>}
												/>
												<Typography sx={{ fontSize: "0.85em" }}>Monthly</Typography>
											</Stack>
											<Stack direction="row">
												<Checkbox
													inputRef={quarterlyFrequency}
													sx={{
														border: `2px solid ${pink}`,
														borderRadius: "0.25em",
														width: "1.2em",
														height: "1.2em",
														marginRight: "0.5em",
														'&.Mui-checked': {
															color: pink
														}
													}}
													icon={<React.Fragment/>}
												/>
												<Typography sx={{ fontSize: "0.85em" }}>Quarterly</Typography>
											</Stack>
										</Stack>
									</Stack>
								</Stack>
							</Box>
							<Box sx={{ width: "50%", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }} alignItems="center">
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Confirm Choices
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1.25em",
											fontSize: "0.8em"
										}}
									>
										Once you confirm your choices, we will set up your preferences<br/>
										and be in touch to arrange scheduling and initiate your Simulated<br/>
										Phishing Programme
									</Typography>
									<Stack
										direction="row"
										sx={{
											width: "15em",
											height: "4em",
											marginTop: "2em",
											borderRadius: "2.5em",
											background: blueGradientBackground,
											"&:hover": {
												cursor: "pointer"
											}
										}}
										alignItems="center"
										justifyContent="center"
										spacing={1}
										onClick={handleSubmitClick}
									>
										<Typography
											sx={{ fontWeight: "bold", color: "#ffffff", paddingLeft: "0.5em" }}
										>
											Set Up Phishing Sim
										</Typography>
										<ArrowCircleUpSharp
											sx={{
												width: "2.5em",
												height: "2.5em",
												transform: "rotate(90deg)",
												color: "#ffffff"
											}}
										/>
									</Stack>
								</Stack>
							</Box>
						</Stack>
					</Stack>
					<SubmitModal
						open={showSubmitModal}
						sentSuccess={sentSuccess}
						onClose={handleSubmitModalClose}
					/>
				</Box>
			</Box>
		</Box>
	);
}

export default PhishingSim;
